/** Volunteer detail collapse. */
import * as React from 'react';
import {
  User,
  Mail,
  Phone,
  BookOpen,
  Clock,
  FileText,
  CheckCircle,
  XCircle,
  Trophy,
  Minus,
  LogOut,
  Building2,
} from 'lucide-react';
import { DetailBadge } from '@/components/common/details/DetailBadge';
import { EmptyPlaceholder } from '@/components/common/details/EmptyPlaceholder';
import { DetailField } from '@/components/common/details/DetailField';
import { DetailChipList } from '@/components/common/details/DetailChipList';
import { DetailsCard } from '@/components/common/DetailsCard';
import { DetailAvailabilityGrid } from '@/components/common/details/DetailAvailabilityGrid';
import type { WeeklyAvailabilitySlot } from '@/types/user.types';
import { rankedContactDetailFields } from '@/utils/user-contact-utils';
import { STAFF_STATUS_CONFIG } from '@/components/volunteers/volunteer-configs';
import { mapVolunteerEstablishmentItems } from '@/components/volunteers/volunteer-table-utils';
import { formatDeactivationDate, getGenderLabel, getStaffStatusColor } from '@/utils/common-utils';
import type { Volunteer } from '@/types/volunteer.types';
import { StaffUserGamificationSection } from '@/components/gamification/StaffUserGamificationSection';
import { normalizeStaffAccountStatus } from '@/types/user.types';

/** Map an establishment to a chip. */
function toEstablishmentChip(e: { id: string; name: string }) {
  return { id: e.id, label: e.name, icon: Building2 };
}

/** Expanded volunteer detail collapse (status, contact, establishments, availability, gamification). */
export const renderVolunteerDetails = (volunteer: Volunteer): React.ReactNode => {
  const statusKey = normalizeStaffAccountStatus(String(volunteer.status));
  const statusConfig = STAFF_STATUS_CONFIG[statusKey] || STAFF_STATUS_CONFIG.active;
  const StatusIcon = statusConfig.icon;

  const subjectNames = volunteer.userProfiles?.[0]?.subjectNames || [];
  const availability = volunteer.userProfiles?.[0]?.availability as
    | { weekly: WeeklyAvailabilitySlot[] }
    | undefined;
  const vscReportsFrequencies = volunteer.userProfiles?.[0]?.vscReportsFrequencies;
  const genderLabel = getGenderLabel(volunteer.userProfiles?.[0]?.gender);
  const exitDate = volunteer.userProfiles?.[0]?.exitDate;
  const isDeactivated = normalizeStaffAccountStatus(String(volunteer.status)) === 'deactivated';

  const establishments = mapVolunteerEstablishmentItems(volunteer.userEstablishments);

  return (
    <>
      <div className="details-view-header">
        <div className="details-view-header-left">
          <div className="details-view-header-icon">
            <User className="w-6 h-6 text-[var(--color-2-nuance-1)]" />
          </div>
          <div className="details-view-header-content">
            <div className="details-view-header-with-badges">
              <h3 className="details-view-header-title">
                {volunteer.firstName} {volunteer.lastName}
              </h3>
              <DetailBadge
                label={statusConfig.label}
                icon={StatusIcon}
                colorName={getStaffStatusColor(volunteer.status)}
                variant="filled"
              />
              {genderLabel && (
                <DetailBadge
                  label={genderLabel}
                  icon={User}
                  colorName="color-2"
                  variant="outlined"
                />
              )}
            </div>
            {volunteer.displayName && (
              <p className="details-view-header-subtitle">{volunteer.displayName}</p>
            )}
          </div>
        </div>
      </div>

      <div className="details-view-grid">
        <DetailsCard icon={User} title="Informations personnelles" colorName="color-2" colSpan={1}>
          <div className="space-y-3">
            {rankedContactDetailFields(
              volunteer.primaryContactKind,
              volunteer.email,
              volunteer.phone,
            ).map((field) => (
              <DetailField
                key={field.label}
                icon={field.iconKind === 'phone' ? Phone : Mail}
                label={field.label}
                value={field.value}
              />
            ))}
            {genderLabel && <DetailField icon={User} label="Genre" value={genderLabel} />}
            {(isDeactivated || exitDate) && (
              <DetailField
                icon={LogOut}
                label="Date de désactivation"
                value={formatDeactivationDate(exitDate)}
              />
            )}
          </div>
        </DetailsCard>

        <DetailsCard icon={Trophy} title="Progression" colorName="color-3" colSpan={1}>
          <StaffUserGamificationSection userId={volunteer.id} />
        </DetailsCard>

        <DetailsCard icon={Building2} title="Établissements" colorName="color-1" colSpan={1}>
          {establishments.length > 0 ? (
            <DetailChipList
              items={establishments.map(toEstablishmentChip)}
              colorScheme="color-1"
              variant="outlined"
            />
          ) : (
            <EmptyPlaceholder message="Aucun établissement affecté" />
          )}
        </DetailsCard>

        <DetailsCard icon={BookOpen} title="Matières accompagnées" colorName="color-3" colSpan={1}>
          {Array.isArray(subjectNames) && subjectNames.length > 0 ? (
            <DetailChipList
              items={subjectNames.map((name, index) => ({
                id: `subject-${index}`,
                label: name,
              }))}
              colorScheme="color-2"
              variant="filled"
            />
          ) : (
            <EmptyPlaceholder message="Aucune matière renseignée" />
          )}
        </DetailsCard>

        <DetailsCard icon={Clock} title="Disponibilités" colorName="color-2" colSpan={2}>
          {availability?.weekly && availability.weekly.length > 0 ? (
            <DetailAvailabilityGrid slots={availability.weekly} variant="compact" />
          ) : (
            <EmptyPlaceholder message="Aucune disponibilité renseignée" />
          )}
        </DetailsCard>

        <DetailsCard
          icon={FileText}
          title="Configuration des rapports VSC"
          colorName="color-3"
          colSpan={1}
        >
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <DetailField
                label="Rapport journalier"
                value={vscReportsFrequencies?.daily ? 'Activé (tous les jours à 20h)' : 'Désactivé'}
              />
              <DetailBadge
                label={vscReportsFrequencies?.daily ? 'Activé' : 'Désactivé'}
                icon={vscReportsFrequencies?.daily ? CheckCircle : XCircle}
                colorName={vscReportsFrequencies?.daily ? 'success' : 'error'}
                variant="filled"
              />
            </div>
            <div className="flex items-center justify-between">
              <DetailField
                label="Rapport hebdomadaire"
                value={vscReportsFrequencies?.weekly ? 'Activé (dimanche à 20h)' : 'Désactivé'}
              />
              <DetailBadge
                label={vscReportsFrequencies?.weekly ? 'Activé' : 'Désactivé'}
                icon={vscReportsFrequencies?.weekly ? CheckCircle : XCircle}
                colorName={vscReportsFrequencies?.weekly ? 'success' : 'error'}
                variant="filled"
              />
            </div>
          </div>
        </DetailsCard>
      </div>

      {volunteer.createdAt && (
        <div className="mt-8 pt-4 border-t border-[var(--color-1-nuance-3)]">
          <div className="flex items-center gap-6 text-xs text-muted-foreground">
            <span>Créé le {new Date(volunteer.createdAt).toLocaleDateString('fr-FR')}</span>
            {volunteer.updatedAt && (
              <>
                <Minus className="w-3 h-3" />
                <span>Modifié le {new Date(volunteer.updatedAt).toLocaleDateString('fr-FR')}</span>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};
